import React from 'react';

/**
 * AltParallaxBackdrop
 *
 * Reusable hero backdrop that renders the same animated parallax forest
 * scene used on the homepage (HomeAlt2).
 *
 * Layered structure:
 *
 *  ┌────────────────────────────────────────────────┐  ← .alt-hp-bg
 *  │  Sky gradient — fills the entire hero section  │     fills section
 *  │                                                │     (any height)
 *  │  ┌──────────────────────────────────────────┐  │
 *  │  │  .alt-hp-scene                           │  │  ← fixed-height
 *  │  │   ┌──────────────────────────────────┐   │  │     trees scene
 *  │  │   │ 6 × parallax PNG layers          │   │  │     (760px from
 *  │  │   │  (mountains → trees → grass)     │   │  │      top of bg)
 *  │  │   └──────────────────────────────────┘   │  │
 *  │  └──────────────────────────────────────────┘  │
 *  │                                                │
 *  │  …rest of section, sky-only…                   │
 *  └────────────────────────────────────────────────┘
 *
 * Why this layout: every page's hero has different content height — a
 * blog title might wrap to four lines, "Contact" is one word — so when
 * the trees were anchored to the BOTTOM of the section, the horizon line
 * landed at different y-positions on different pages. By anchoring the
 * trees to a fixed-height inner scene at the TOP, every page renders the
 * forest horizon at exactly the same offset from the top of the page.
 *
 * Use `min-h-[700px] md:min-h-[760px]` on the parent <section> so even
 * the short pages (Contact, MediaPress) are tall enough for the full
 * scene to be visible.
 */
const AltParallaxBackdrop = () => (
  <>
    <style>{`
      .alt-hp-bg {
        position: absolute;
        inset: 0;
        overflow: hidden;
        pointer-events: none;
        z-index: 0;
        /* Sky gradient is rendered ONLY in the top 700px (mobile) / 760px
           (desktop) — exactly matching .alt-hp-scene. Anything BELOW the
           scene is a solid dark navy that visually continues the forest
           floor, so on tall hero sections (Founder portrait, pages with
           big stat grids) the bottom of the section is a clean dark band
           instead of the olive-yellow gradient tail bleeding through. */
        background:
          radial-gradient(circle at 20% 15%, #ffffff2e, transparent 42%) top / 100% 700px no-repeat,
          linear-gradient(#84b5f3 0%, #d4f2ff 35%, #f0e9dc 62%, #d4e3b2 100%) top / 100% 700px no-repeat,
          #0b2c57;
      }
      @media (min-width: 768px) {
        .alt-hp-bg {
          background:
            radial-gradient(circle at 20% 15%, #ffffff2e, transparent 42%) top / 100% 760px no-repeat,
            linear-gradient(#84b5f3 0%, #d4f2ff 35%, #f0e9dc 62%, #d4e3b2 100%) top / 100% 760px no-repeat,
            #0b2c57;
        }
      }
      /* Trees scene — fixed height. Anchors all parallax layers to a
         consistent vertical region of the page so the horizon never
         shifts between routes. */
      .alt-hp-scene {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 700px;
      }
      @media (min-width: 768px) {
        .alt-hp-scene { height: 760px; }
      }
      .alt-hp-layer {
        position: absolute;
        inset: 0;
        background-position: 0 100%;
        background-repeat: repeat-x;
        animation-iteration-count: infinite;
        animation-timing-function: linear;
        opacity: .95;
        pointer-events: none;
        will-change: background-position;
      }
      .alt-hp-l2 { background-image: url('/parallax/2.png'); background-size: auto 145px; animation: alt_hp_drift 30s linear infinite;  z-index: 4; }
      .alt-hp-l3 { background-image: url('/parallax/3.png'); background-size: auto 158px; animation: alt_hp_drift 55s linear infinite;  z-index: 3; }
      .alt-hp-l4 { background-image: url('/parallax/4.png'); background-size: auto 468px; animation: alt_hp_drift 75s linear infinite;  z-index: 1; }
      .alt-hp-l5 { background-image: url('/parallax/5.png'); background-size: auto 311px; animation: alt_hp_drift 95s linear infinite;  z-index: 1; }
      .alt-hp-l6 { background-image: url('/parallax/6.png'); background-size: auto 222px; animation: alt_hp_drift 120s linear infinite; z-index: 1; }
      @keyframes alt_hp_drift {
        from { background-position: 2765px 100%; }
        to   { background-position: 550px 100%; }
      }
    `}</style>
    <div className="alt-hp-bg" aria-hidden="true" data-testid="alt-parallax-backdrop">
      <div className="alt-hp-scene">
        <div className="alt-hp-layer alt-hp-l6" />
        <div className="alt-hp-layer alt-hp-l5" />
        <div className="alt-hp-layer alt-hp-l4" />
        <div className="alt-hp-layer alt-hp-l3" />
        <div className="alt-hp-layer alt-hp-l2" />
      </div>
    </div>
  </>
);

export default AltParallaxBackdrop;
